import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Save, X } from "lucide-react";

/**
 * Generic create / edit / view modal driven by a `fields` description.
 *
 * Field shape:
 * {
 *   name, label, type: 'text'|'number'|'textarea'|'select'|'checkbox'|'datalist',
 *   required, placeholder, help, fullWidth,
 *   options: [{ value, label }],                  // static select / datalist options
 *   optionsKey, optionsLoader: async () => [...], // async options (react-query)
 *   createOnly, editOnly, readOnlyOnEdit,
 * }
 */
export default function AdminFormModal({
  open,
  mode = "create", // 'create' | 'edit' | 'view'
  title,
  subtitle,
  icon: Icon,
  fields,
  initialValues,
  onClose,
  onSubmit,
  isSubmitting = false,
  submitLabel,
  size = "max-w-2xl",
}) {
  const readOnly = mode === "view";
  const visibleFields = useMemo(
    () =>
      fields.filter((f) => {
        if (f.createOnly && mode !== "create") return false;
        if (f.editOnly && mode === "create") return false;
        return true;
      }),
    [fields, mode]
  );

  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;

  useEffect(() => {
    if (!open) return;
    const base = {};
    fieldsRef.current.forEach((f) => {
      const initial = initialValues?.[f.name];
      if (f.type === "checkbox") base[f.name] = Boolean(initial);
      else base[f.name] = initial === undefined || initial === null ? "" : initial;
    });
    setValues(base);
    setErrors({});
  }, [open, initialValues]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const setValue = (name, value) => {
    setValues((v) => ({ ...v, [name]: value }));
    if (errors[name]) setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (readOnly) return onClose();
    const nextErrors = {};
    visibleFields.forEach((f) => {
      const v = values[f.name];
      if (f.required && f.type !== "checkbox" && (v === "" || v === null || v === undefined)) {
        nextErrors[f.name] = `${f.label} is required`;
      }
      if (f.type === "number" && v !== "" && v !== null && v !== undefined && Number.isNaN(Number(v))) {
        nextErrors[f.name] = `${f.label} must be a number`;
      }
    });
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    // Normalise: trim strings, cast numbers, empty -> null
    const out = {};
    visibleFields.forEach((f) => {
      let v = values[f.name];
      if (f.type === "checkbox") out[f.name] = Boolean(v);
      else if (f.type === "number") out[f.name] = v === "" || v === null || v === undefined ? null : Number(v);
      else if (typeof v === "string") {
        const t = v.trim();
        out[f.name] = t === "" ? null : t;
      } else out[f.name] = v ?? null;
    });
    onSubmit(out);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${size} max-h-[92vh] flex flex-col overflow-hidden`}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-cyan-50 to-white">
          <div className="flex items-center gap-3">
            {Icon && (
              <div className="w-9 h-9 rounded-full bg-cyan-100 flex items-center justify-center">
                <Icon className="w-4 h-4 text-cyan-600" />
              </div>
            )}
            <div>
              <h2 className="text-lg font-bold text-gray-900">{title}</h2>
              {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {visibleFields.map((f) => (
              <div key={f.name} className={f.fullWidth || f.type === "textarea" ? "sm:col-span-2" : ""}>
                <FieldInput
                  field={f}
                  value={values[f.name]}
                  error={errors[f.name]}
                  readOnly={readOnly || (mode === "edit" && f.readOnlyOnEdit)}
                  onChange={(v) => setValue(f.name, v)}
                />
              </div>
            ))}
          </div>

          <div className="flex gap-3 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {readOnly ? "Close" : "Cancel"}
            </button>
            {!readOnly && (
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {isSubmitting ? "Saving..." : submitLabel || (mode === "edit" ? "Save Changes" : "Create")}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

const inputClass =
  "w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none bg-white disabled:bg-gray-50 disabled:text-gray-500";

function FieldInput({ field, value, error, readOnly, onChange }) {
  const { data: loadedOptions = [], isLoading } = useQuery({
    queryKey: ["formOptions", field.optionsKey || field.name],
    queryFn: field.optionsLoader,
    enabled: Boolean(field.optionsLoader),
    staleTime: 5 * 60 * 1000,
  });
  const options = field.optionsLoader ? loadedOptions : field.options || [];

  const label = (
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      {field.label}
      {field.required && !readOnly && <span className="text-red-500 ml-0.5">*</span>}
    </label>
  );

  if (readOnly) {
    let display = value;
    if (field.type === "checkbox") display = value ? "Yes" : "No";
    else if (field.type === "select") {
      const found = options.find((o) => String(o.value) === String(value));
      display = found ? found.label : value;
    }
    return (
      <div>
        {label}
        <div className="px-3 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-800 min-h-[42px] whitespace-pre-wrap break-words">
          {display === "" || display === null || display === undefined ? (
            <span className="text-gray-400">—</span>
          ) : (
            String(display)
          )}
        </div>
      </div>
    );
  }

  let control;
  switch (field.type) {
    case "textarea":
      control = (
        <textarea
          rows={field.rows || 3}
          value={value ?? ""}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass}
        />
      );
      break;
    case "select":
      control = (
        <select
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass}
          disabled={isLoading}
        >
          <option value="">{isLoading ? "Loading..." : field.placeholder || `-- Select ${field.label.toLowerCase()} --`}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
      break;
    case "checkbox":
      control = (
        <label className="inline-flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 border border-gray-200 rounded-lg bg-white cursor-pointer select-none">
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            className="w-4 h-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
          />
          {field.checkboxLabel || field.label}
        </label>
      );
      break;
    case "datalist": {
      const listId = `dl-${field.name}`;
      control = (
        <>
          <input
            type="text"
            list={listId}
            value={value ?? ""}
            placeholder={field.placeholder}
            onChange={(e) => onChange(e.target.value)}
            className={inputClass}
            autoComplete="off"
          />
          <datalist id={listId}>
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </datalist>
        </>
      );
      break;
    }
    case "number":
      control = (
        <input
          type="number"
          value={value ?? ""}
          placeholder={field.placeholder}
          min={field.min}
          step={field.step || 1}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass}
        />
      );
      break;
    default:
      control = (
        <input
          type="text"
          value={value ?? ""}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass}
          maxLength={field.maxLength}
        />
      );
  }

  return (
    <div>
      {field.type === "checkbox" ? (
        <div className="block text-sm font-medium text-gray-700 mb-1.5">{field.label}</div>
      ) : (
        label
      )}
      {control}
      {field.help && !error && <p className="text-xs text-gray-400 mt-1">{field.help}</p>}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}
